import { EntityType } from '@shared/models/entity-enum';
import {
  AvailabilityColumns,
  CompanyColumns,
  ContactColumns,
  LeaseColumns,
  PropertyColumns,
  PropertySections,
  SaleColumns,
  TenancyColumns,
  TimColumns,
} from '@miq/catalog';
import { QueryCriteria } from './shared/search-filters.model';
import { isAExternalAvailabilityReviewer } from '@shared/resolvers/user-info.resolver';
import { IOption } from '@shared/models/icommons.model';
import { pick } from '@shared/functions';
import { BrokerUserRole, isBrokerRole } from '@miq/user';

export interface SearchTab {
  tabName: string;
  mounted: boolean;
  viewEnabled: string;
  searchBarPlaceholder: string;
  aggregation?: any;
  state?: any;
  columnHeaders?: string[];
  textSearchFields?: string[];
  typeAhead?: boolean;
  isUsageFilterable?: boolean;
  isSubmarketFilterable?: boolean;
  hasMapEnabled?: boolean;
  restrictions?: Restriction;
  position?: number;
  enabledViews?: string[];
}

export interface Restriction {
  filters: RestrictionFilter[];
}

export interface RestrictionFilter {
  isEnabled: boolean;
  field: string;
  key?: number;
  value: QueryCriteria;
  categoryTitle: string;
}

export const DEFAULT_GEOGRAPHY_VALUES = {
  markets: [],
  propertyTypes: [],
  submarkets: [],
  districts: [],
  neighborhoods: [],
};

type MapOfQueryCriteria = { [key: string]: QueryCriteria };

export const DEFAULT_FILTERS_PER_ENTITY: { [key: string]: MapOfQueryCriteria } = {
  [EntityType.Property]: {
    3724: {
      key: 3724,
      type: 'equal',
      field: 'edp_match_verified_f',
      dataType: 'boolean',
      name: 'EDP Match Verified',
    },
    839: {
      key: 839,
      type: 'multiNotEqual',
      field: 'ref_property_status_desc',
      dataType: 'text',
      name: 'Status',
    },
    16933: {
      key: 16933,
      type: 'equal',
      field: 'miq_record_hide_f',
      dataType: 'boolean',
      name: 'Hidden Properties',
    },
  },
  [EntityType.Availability]: {
    3727: {
      key: 3727,
      type: 'equal',
      field: 'edp_match_verified_f',
      dataType: 'boolean',
      name: 'EDP Match Verified',
    },
    2: {
      key: 2,
      type: 'multiNotEqual',
      field: 'ref_space_availability_status_desc',
      dataType: 'text',
      name: 'Availability Status',
    },
    17026: {
      key: 17026,
      type: 'equal',
      field: 'miq_record_hide_f',
      dataType: 'boolean',
      name: 'Hidden Availability Records',
    },
  },
  [EntityType.LeaseComp]: {
    1750: {
      key: 1750,
      type: 'equal',
      field: 'edp_match_verified_f',
      dataType: 'boolean',
      name: 'EDP Match Verified',
    },
    1743: {
      key: 1743,
      type: 'multiNotEqual',
      field: 'ref_arrangement_status_desc',
      dataType: 'text',
      name: 'Comp Status',
    },
    17029: {
      key: 17029,
      type: 'equal',
      field: 'miq_record_hide_f',
      dataType: 'boolean',
      name: 'Hidden Lease Records',
    },
  },
  [EntityType.SaleComp]: {
    2298: {
      key: 2298,
      type: 'equal',
      field: 'edp_match_verified_f',
      dataType: 'boolean',
      name: 'EDP Match Verified',
    },
    2273: {
      key: 2273,
      type: 'multiNotEqual',
      field: 'ref_arrangement_status_desc',
      dataType: 'text',
      name: 'Comp Status',
    },
    17030: {
      key: 17030,
      type: 'equal',
      field: 'miq_record_hide_f',
      dataType: 'boolean',
      name: 'Hidden Sale Records',
    },
  },
  [EntityType.Tim]: {
    16071: {
      key: 16071,
      type: 'multiNotEqual',
      field: 'status_type_desc',
      dataType: 'text',
      name: 'TIM Status',
    },
    17027: {
      key: 17027,
      type: 'equal',
      field: 'miq_record_hide_f',
      dataType: 'boolean',
      name: 'Hidden TIM Records',
    },
  },
  [EntityType.Company]: {
    1364: {
      key: 1364,
      type: 'equal',
      field: 'edp_match_verified_f',
      dataType: 'boolean',
      name: 'EDP Match Verified',
    },
    1359: { key: 1359, type: 'multiNotEqual', field: 'ref_party_status_desc', dataType: 'text', name: 'Status' },
    18746: { key: 18746, type: 'multi', field: 'system_tag/name', dataType: 'text', name: 'Company Isolation' },
  },
  [EntityType.Contact]: {
    1508: {
      key: 1508,
      type: 'equal',
      field: 'edp_match_verified_f',

      dataType: 'boolean',
      name: 'EDP Match Verified',
    },
  },
  [EntityType.Tenancy]: {
    3785: {
      key: 3785,
      type: 'multiNotEqual',
      field: 'property_space/tenancy_status',
      dataType: 'text',
      name: 'Status',
    },
    17028: {
      key: 17028,
      type: 'equal',
      field: 'miq_record_hide_f',
      dataType: 'boolean',
      name: 'Hidden Tenancy Records',
    },
  },
};

// create repository of `MapOfQueryCriteria` by Entity
const PropertyFilters: MapOfQueryCriteria = DEFAULT_FILTERS_PER_ENTITY[EntityType.Property];
const CompanyFilters: MapOfQueryCriteria = DEFAULT_FILTERS_PER_ENTITY[EntityType.Company];
const ContactFilters: MapOfQueryCriteria = DEFAULT_FILTERS_PER_ENTITY[EntityType.Contact];
const LeaseCompFilters: MapOfQueryCriteria = DEFAULT_FILTERS_PER_ENTITY[EntityType.LeaseComp];
const SaleCompFilters: MapOfQueryCriteria = DEFAULT_FILTERS_PER_ENTITY[EntityType.SaleComp];
const AvailabilityFilters: MapOfQueryCriteria = DEFAULT_FILTERS_PER_ENTITY[EntityType.Availability];
const TenancyFilters: MapOfQueryCriteria = DEFAULT_FILTERS_PER_ENTITY[EntityType.Tenancy];
const TimFilters: MapOfQueryCriteria = DEFAULT_FILTERS_PER_ENTITY[EntityType.Tim];

/**
 * Given a criteria map, returns a function to resolve the filter object of given columnId
 * and adds given `values` to QueryCriteria object to be returned
 *
 * @param queryCriteriaMap
 * @returns
 */
const restrictedQueryCriteriaMakerFactory =
  (queryCriteriaMap: MapOfQueryCriteria) => (columnId: number, values: any) => ({
    ...queryCriteriaMap[columnId],
    values,
  });

// create the QueryCriteria's factory functions by entity

// Property
const makePropertyRestrictionFilter = restrictedQueryCriteriaMakerFactory(PropertyFilters);

// Availability
const makeAvailabilityRestriction = restrictedQueryCriteriaMakerFactory(AvailabilityFilters);

// Lease
const makeLeaseRestrictionFilter = restrictedQueryCriteriaMakerFactory(LeaseCompFilters);

// SaleComp
const makeSaleCompRestrictionFilter = restrictedQueryCriteriaMakerFactory(SaleCompFilters);

// Tim
const makeTimRestrictionFilter = restrictedQueryCriteriaMakerFactory(TimFilters);

// Company
const makeCompanyRestrictionFilter = restrictedQueryCriteriaMakerFactory(CompanyFilters);

// Contact
const makeContactRestrictionFilter = restrictedQueryCriteriaMakerFactory(ContactFilters);

// Tenancy
const makeTenancyRestrictionFilter = restrictedQueryCriteriaMakerFactory(TenancyFilters);

/**
 * Restriction Filters Map Per Entity
 */
const RESTRICTION_FILTERS_PER_ENTITY = {
  [EntityType.Property]: [
    {
      isEnabled: false,
      key: PropertyColumns.Admin_EDPMatchVerified,
      field: 'edp_match_verified_f',
      value: makePropertyRestrictionFilter(PropertyColumns.Admin_EDPMatchVerified, 'true'),
      categoryTitle: 'Admin',
    },
    {
      isEnabled: true,
      key: PropertyColumns.PropertyDetails_Status,
      field: 'ref_property_status_desc',
      value: makePropertyRestrictionFilter(PropertyColumns.PropertyDetails_Status, [
        'Deleted',
        'Demolished',
        'Inactive',
        'Converted',
      ]),
      categoryTitle: 'Building Details',
    },
    {
      isEnabled: true,
      key: PropertyColumns.Admin_HideProperty,
      field: 'miq_record_hide_f',
      value: makePropertyRestrictionFilter(PropertyColumns.Admin_HideProperty, 'false'),
      categoryTitle: 'Admin',
    },
  ],
  [EntityType.Availability]: [
    {
      isEnabled: false,
      key: AvailabilityColumns.Admin_EDPMatchVerified,
      field: 'edp_match_verified_f',
      value: makeAvailabilityRestriction(AvailabilityColumns.Admin_EDPMatchVerified, 'true'),
      categoryTitle: 'Admin',
    },
    {
      isEnabled: true,
      key: AvailabilityColumns.SpaceDetails_AvailabilityStatus,
      field: 'ref_space_availability_status_desc',
      value: makeAvailabilityRestriction(
        AvailabilityColumns.SpaceDetails_AvailabilityStatus,
        (() => {
          const baseValues = ['Deleted', 'Off Market', 'Renewed', 'Sold', 'Withdrawn'];
          const externalValues = ['Unconfirmed – Active', 'Unconfirmed – Off Market', 'Unconfirmed – Rejected'];
          return isAExternalAvailabilityReviewer() ? [...baseValues, ...externalValues] : baseValues;
        })()
      ),
      categoryTitle: 'Space Details',
    },
    {
      isEnabled: true,
      key: 17026,
      field: 'miq_record_hide_f',
      value: makeAvailabilityRestriction(17026, 'false'),
      categoryTitle: 'Admin',
    },
  ],
  [EntityType.LeaseComp]: [
    {
      isEnabled: false,
      key: LeaseColumns.Admin_EDPMatchVerified,
      field: 'edp_match_verified_f',
      value: makeLeaseRestrictionFilter(LeaseColumns.Admin_EDPMatchVerified, 'true'),
      categoryTitle: 'Admin',
    },
    {
      isEnabled: true,
      key: LeaseColumns.Admin_CompStatus,
      field: 'ref_arrangement_status_desc',
      value: makeLeaseRestrictionFilter(LeaseColumns.Admin_CompStatus, ['Inactive']),
      categoryTitle: 'Admin',
    },
    {
      isEnabled: true,
      key: LeaseColumns.Admin_HideLease,
      field: 'miq_record_hide_f',
      value: makeLeaseRestrictionFilter(LeaseColumns.Admin_HideLease, 'false'),
      categoryTitle: 'Admin',
    },
  ],
  [EntityType.SaleComp]: [
    {
      isEnabled: false,
      key: SaleColumns.Admin_EDPMatchVerified,
      field: 'edp_match_verified_f',
      value: makeSaleCompRestrictionFilter(SaleColumns.Admin_EDPMatchVerified, 'true'),
      categoryTitle: 'Admin',
    },
    {
      isEnabled: true,
      key: SaleColumns.Admin_CompStatus,
      field: 'ref_arrangement_status_desc',
      value: makeSaleCompRestrictionFilter(SaleColumns.Admin_CompStatus, ['Inactive']),
      categoryTitle: 'Admin',
    },
    {
      isEnabled: true,
      key: 17030,
      field: 'miq_record_hide_f',
      value: makeSaleCompRestrictionFilter(17030, 'false'),
      categoryTitle: 'Admin',
    },
  ],
  [EntityType.Tim]: [
    {
      isEnabled: true,
      key: TimColumns.TIMDetails_TIMStatus,
      field: 'status_type_desc',
      value: makeTimRestrictionFilter(TimColumns.TIMDetails_TIMStatus, ['Closed']),
      categoryTitle: 'TIM Detail',
    },
    {
      isEnabled: true,
      key: 17027,
      field: 'miq_record_hide_f',
      value: makeTimRestrictionFilter(17027, 'false'),
      categoryTitle: 'Admin',
    },
  ],
  [EntityType.Company]: [
    {
      isEnabled: false,
      key: CompanyColumns.Admin_EDPMatchVerified,
      field: 'edp_match_verified_f',
      value: makeCompanyRestrictionFilter(CompanyColumns.Admin_EDPMatchVerified, 'true'),
      categoryTitle: 'Admin',
    },
    {
      isEnabled: true,
      key: CompanyColumns.Admin_Status,
      field: 'ref_party_status_desc',
      value: makeCompanyRestrictionFilter(CompanyColumns.Admin_Status, ['Inactive']),
      categoryTitle: 'Admin',
    },
    {
      isEnabled: true,
      key: CompanyColumns.Company_Isolation,
      field: 'system_tag/name',
      value: makeCompanyRestrictionFilter(CompanyColumns.Company_Isolation, ['DummyValue']),
      categoryTitle: 'Admin',
    },
  ],
  [EntityType.Contact]: [
    {
      isEnabled: false,
      key: ContactColumns.Admin_EDPMatchVerified,
      field: 'edp_match_verified_f',
      value: makeContactRestrictionFilter(ContactColumns.Admin_EDPMatchVerified, 'true'),
      categoryTitle: 'Admin',
    },
  ],
  [EntityType.Tenancy]: [
    {
      isEnabled: true,
      key: TenancyColumns.FloorSuite_Status,
      field: 'property_space/tenancy_status',
      value: makeTenancyRestrictionFilter(TenancyColumns.FloorSuite_Status, ['Expired', 'Gone', 'No Longer Tenant']),
      categoryTitle: 'Floor/Suite',
    },
    {
      isEnabled: true,
      key: TenancyColumns.Admin_HideTenancy,
      field: 'miq_record_hide_f',
      value: makeTenancyRestrictionFilter(TenancyColumns.Admin_HideTenancy, 'false'),
      categoryTitle: 'Admin',
    },
  ],
  [EntityType.PortfolioSale]: [
    // empty but kept as placeholder
  ],
  [EntityType.Brand]: [
    // empty but kept as placeholder
  ],
};

/**
 * Tab configuration object, it holds information like title, position whether is mounted or not plus
 * restriction filters by entity
 *
 */
export const baseTabMap: SearchTab[] = [
  {
    /*
     * This entry is to fit with tab indexes (they star at 1 because "Records title" is a fake tab)
     * therefore we need this entry to match with calculated tabIndexes
     * better could be that "records" title were not a "fake tab",
     *
     * TODO: DEVELOPER NOTE: THIS should be considered when developing the new navigation
     */
    tabName: 'records title',
    mounted: true,
    viewEnabled: 'none',
    searchBarPlaceholder: 'records title',
    position: 0,
  },
  {
    tabName: 'records',
    mounted: false,
    viewEnabled: 'both',
    searchBarPlaceholder: ``,
    aggregation: {},
    hasMapEnabled: true,
  },
  {
    tabName: 'property',
    mounted: false,
    isUsageFilterable: true,
    isSubmarketFilterable: true,
    viewEnabled: 'both',
    searchBarPlaceholder: 'recordsSearch.searchPropertyPlaceHolder',
    aggregation: {},
    restrictions: {
      filters: RESTRICTION_FILTERS_PER_ENTITY[EntityType.Property],
    },
    hasMapEnabled: true,
    enabledViews: ['table', 'map', 'both'],
  },
  {
    tabName: 'company',
    mounted: false,
    isUsageFilterable: false,
    isSubmarketFilterable: true,
    viewEnabled: 'both',
    searchBarPlaceholder: 'recordsSearch.searchCompanyPlaceHolder',
    aggregation: {},
    restrictions: {
      filters: RESTRICTION_FILTERS_PER_ENTITY[EntityType.Company],
    },
    hasMapEnabled: true,
    enabledViews: ['table', 'map', 'both'],
  },
  {
    tabName: 'brand',
    mounted: false,
    isUsageFilterable: true,
    isSubmarketFilterable: true,
    viewEnabled: 'table',
    searchBarPlaceholder: 'recordsSearch.searchBrandPlaceHolder',
    aggregation: {},
    restrictions: {
      filters: RESTRICTION_FILTERS_PER_ENTITY[EntityType.Brand],
    },
    hasMapEnabled: false,
    enabledViews: ['table', 'map', 'both'],
  },
  {
    tabName: 'contact',
    mounted: false,
    isUsageFilterable: false,
    isSubmarketFilterable: true,
    viewEnabled: 'both', // * Prevent to show the map
    searchBarPlaceholder: 'recordsSearch.searchContactPlaceHolder',
    aggregation: {},
    restrictions: {
      filters: RESTRICTION_FILTERS_PER_ENTITY[EntityType.Contact],
    },
    hasMapEnabled: true,
    enabledViews: ['table', 'map', 'both'],
  },
  {
    tabName: 'lease',
    mounted: false,
    isUsageFilterable: true,
    isSubmarketFilterable: true,
    viewEnabled: 'both',
    searchBarPlaceholder: 'recordsSearch.searchLeasePlaceHolder',
    aggregation: {},
    restrictions: {
      filters: RESTRICTION_FILTERS_PER_ENTITY[EntityType.LeaseComp],
    },
    hasMapEnabled: true,
    enabledViews: ['table', 'map', 'both'],
  },
  {
    tabName: 'sale',
    mounted: false,
    isUsageFilterable: true,
    isSubmarketFilterable: true,
    viewEnabled: 'both',
    searchBarPlaceholder: 'recordsSearch.searchSalePlaceHolder',
    aggregation: {},
    restrictions: {
      filters: RESTRICTION_FILTERS_PER_ENTITY[EntityType.SaleComp],
    },
    hasMapEnabled: true,
    enabledViews: ['table', 'map', 'both'],
  },
  {
    tabName: 'portfoliosale',
    mounted: false,
    isUsageFilterable: true,
    isSubmarketFilterable: true,
    viewEnabled: 'both',
    searchBarPlaceholder: 'recordsSearch.searchPortfolioSalePlaceHolder',
    aggregation: {},
    restrictions: {
      filters: RESTRICTION_FILTERS_PER_ENTITY[EntityType.PortfolioSale],
    },
    hasMapEnabled: true,
    enabledViews: ['table', 'map', 'both'],
  },
  {
    tabName: 'availability',
    mounted: false,
    isUsageFilterable: true,
    isSubmarketFilterable: true,
    viewEnabled: 'both',
    searchBarPlaceholder: 'recordsSearch.searchAvailabilityPlaceHolder',
    aggregation: {},
    restrictions: {
      filters: RESTRICTION_FILTERS_PER_ENTITY[EntityType.Availability],
    },
    hasMapEnabled: true,
    enabledViews: ['table', 'map', 'both'],
  },
  {
    tabName: 'tim',
    mounted: false,
    isUsageFilterable: true,
    isSubmarketFilterable: false,
    viewEnabled: 'table',
    searchBarPlaceholder: 'recordsSearch.searchTimPlaceHolder',
    aggregation: {},
    restrictions: {
      filters: RESTRICTION_FILTERS_PER_ENTITY[EntityType.Tim],
    },
    hasMapEnabled: false,
    enabledViews: ['table'],
  },
  {
    tabName: 'tenancy',
    mounted: false,
    isUsageFilterable: true,
    isSubmarketFilterable: true,
    viewEnabled: 'both',
    searchBarPlaceholder: 'recordsSearch.searchTenancyPlaceHolder',
    aggregation: {},
    restrictions: {
      filters: RESTRICTION_FILTERS_PER_ENTITY[EntityType.Tenancy],
    },
    hasMapEnabled: true,
    enabledViews: ['table', 'map', 'both'],
  },
];

export const baseFilterInitialState = {
  availability: [],
  property: [],
  company: [],
  lease: [],
  sale: [],
  portfoliosale: [],
  contact: [],
  tim: [],
  tenancy: [],
  brand: [],
};

export const DEFAULT_GEO_LABELS = {
  country: 'recordsSearch.country',
  district: 'recordsSearch.district',
  globalRegion: 'recordsSearch.globalRegion',
  market: 'recordsSearch.markets',
  neighborhood: 'recordsSearch.neighborhood',
  division: 'recordsSearch.division',
  submarket: 'recordsSearch.submarket',
};

export const postalAddressPath = {
  property: 'postal_address',
  company: 'location/postal_address',
  lease_comp: 'property.postal_address',
  sale_comp: 'property.postal_address',
  availability: 'property.postal_address',
  tim: 'postal_address',
};

export const HOVER_LIMIT = 50;

export enum usagesAttributes {
  ClassType = 844,
  SubmarketDesc = 862,
  UsageTypeDesc = 1050,
}

export const usagesAttributesInfo = {
  [usagesAttributes.ClassType]: {
    field: 'usages/ref_property_class_type_desc',
  },
  [usagesAttributes.SubmarketDesc]: {
    field: 'usages/lig_geography.submarket_desc',
  },
  [usagesAttributes.UsageTypeDesc]: {
    field: 'usages/statistical_f',
  },
};

export const groupAttributesByEntity = {
  [EntityType.Availability]: {
    parent: 'property',
    child: 'availabilities',
  },
};

export const OVERLAPPING_FILTERS_ASSOCIATIONS = {
  [EntityType.Property]: {
    [PropertySections.Companies]: [PropertyColumns.CompanyAgreementSign],
  },
};

export const getGroupKey = (associations: IOption[]) =>
  associations
    .map(pick('key'))
    .sort((a, b) => a - b)
    .join('_');

export const GROUP_CONFIGURATIONS = {
  [EntityType.Property]: {
    [PropertySections.Companies]: {
      [getGroupKey([
        { key: PropertyColumns.CompanyAgreementSign } as IOption,
        { key: PropertyColumns.CompanyAgreementEnd } as IOption,
        { key: PropertyColumns.CompanyAgreementStart } as IOption,
        { key: PropertyColumns.Companies_Role } as IOption,
        { key: PropertyColumns.Companies_NameCompany } as IOption,
        { key: PropertyColumns.CompanyAgreementStatus } as IOption,
      ])]: {
        selectTogheter: [
          PropertyColumns.CompanyAgreementSign,
          PropertyColumns.CompanyAgreementEnd,
          PropertyColumns.CompanyAgreementStart,
          PropertyColumns.CompanyAgreementStatus,
        ],
        order: [
          PropertyColumns.CompanyAgreementSign,
          PropertyColumns.CompanyAgreementStart,
          PropertyColumns.CompanyAgreementEnd,
          PropertyColumns.CompanyAgreementStatus,
          PropertyColumns.Companies_NameCompany,
          PropertyColumns.Companies_Role,
        ],
      },
    },
  },
};

export type DefaultFilterOption = {
  condition: (ctx: any) => boolean;
  defaultFilterQuery: {
    type: string;
    field: string;
    key: number;
    values: any[];
    dataType: string;
    name: string;
  };
  allOptions: any[];
  defaultSelectedValues: any[];
};

// Default filter options for entity attribute based on condition
export const DEFAULT_FILTER_OPTIONS: { [key in EntityType.LeaseComp]?: DefaultFilterOption[] } = {
  [EntityType.LeaseComp]: [
    {
      // Context can be anything that is needed to evaluate the condition. Now it is user info and can be enhanced further
      condition: ctx => {
        const userInfo = ctx.userInfo;
        return isBrokerRole(userInfo?.roleVal as BrokerUserRole);
      },
      // When filter is not added, but still want to filter by some default values
      defaultFilterQuery: {
        type: 'multi',
        field: 'lease_record_type_desc',
        key: 423,
        values: ['Lease Comp', 'Partial Lease Comp'],
        dataType: 'text',
        name: 'Record Type',
      },
      // All values to be applied when condition is met
      allOptions: ['Lease Comp', 'Partial Lease Comp', 'Owner Occupied'],
      // Selected values to be applied when condition is met
      defaultSelectedValues: ['Lease Comp', 'Partial Lease Comp'],
    },
  ],
};

/**
 * Compound filter configuration keyed by parent filter `key`.
 *
 * Populated in `./filter-set/filter-option/compound-filter-groups.config.ts` (translated from
 * the pre-existing `develop`-branch configuration, see `docs/old-compound-filters.ts`).
 * Re-exported here since this is where the rest of the module already imports it from.
 *
 * Each entry defines:
 *  - `groups`             Visual grouping of compound filter items in the gear-icon menu
 *                         (optional — flat list rendered when omitted). Items only need a
 *                         `key`; display labels are backfilled from the live BE catalog.
 *  - `defaultSelectedKeys` Keys enforced as minimum selection when the user deselects all.
 *  - `initialSelectedKeys` Keys pre-seeded on first add, overriding the out-of-the-box
 *                         "select all" default (rarely needed — see compound-filter.factory.ts).
 *
 * A filter with server-provided compoundFilters but NO entry here still works out-of-the-box
 * (auto-generated flat groups, "select all" on first add, "first catalog key" on deselect-all)
 * — entries here are customizations, not a requirement.
 */
export { COMPOUND_FILTER_CONFIGURATIONS } from './filter-set/filter-option/compound-filter-groups.config';
